import React from 'react';
import { Link } from 'react-router-dom';

interface HeroSlideProps {
  imageUrl: string;
  heading: string;
  subheading: string;
  ctaText: string;
  ctaLink: string;
  buttonColor?: string;
  isActive: boolean;
}

export default function HeroSlide({
  imageUrl,
  heading,
  subheading,
  ctaText,
  ctaLink,
  buttonColor,
  isActive,
}: HeroSlideProps) {
  const [hasAnimated, setHasAnimated] = React.useState(false);
  // Trigger animation on mount if active, or when becoming active
  React.useEffect(() => {
    if (isActive && !hasAnimated) {
      // Small delay to ensure animation plays on initial load
      const timer = setTimeout(() => {
        setHasAnimated(true);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isActive, hasAnimated]);

  const shouldAnimate = isActive && hasAnimated;

  // Banner links may be internal SPA routes or external URLs.
  const isExternal = /^https?:\/\//i.test(ctaLink);
  const ctaClassName = `inline-flex items-center justify-center gap-2 px-10 py-4 text-base md:text-lg font-semibold text-white rounded-lg transition-transform duration-200 ease-out hover:scale-110 active:scale-95 ${!buttonColor ? 'bg-brand-primary' : ''}`;
  const ctaStyle = buttonColor ? { backgroundColor: buttonColor } : undefined;
  const ctaInner = (
    <>
      {ctaText}
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
      </svg>
    </>
  );

  return (
    <div
      className={`absolute inset-0 transition-all duration-1000 ease-out ${
        isActive ? 'opacity-100 z-10' : 'opacity-0 z-0'
      }`}
    >
      {/* Background Image */}
      <div
        className={`absolute inset-0 bg-cover bg-center transition-transform duration-[8000ms] ease-out ${
          shouldAnimate ? 'scale-100' : 'scale-105'
        }`}
        style={{ backgroundImage: `url(${imageUrl})` }}
      />

      {/* Gradient Overlay for text readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50" />

      {/* Content */}
      <div className="relative h-full flex items-center justify-center">
        <div className="storefront-container text-center">
          <h1
            className={`text-4xl md:text-5xl lg:text-7xl font-bold text-white mb-6 transition-all duration-700 ease-out ${
              shouldAnimate
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 -translate-y-8'
            }`}
            style={{ transitionDelay: shouldAnimate ? '200ms' : '0ms' }}
          >
            {heading}
          </h1>
          <p
            className={`text-lg md:text-xl lg:text-2xl text-white/90 mb-10 max-w-3xl mx-auto transition-all duration-700 ease-out ${
              shouldAnimate
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 -translate-y-8'
            }`}
            style={{ transitionDelay: shouldAnimate ? '400ms' : '0ms' }}
          >
            {subheading}
          </p>
          <div
            className={`transition-[opacity,transform] duration-700 ease-out ${
              shouldAnimate
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 translate-y-8'
            }`}
            style={{ transitionDelay: shouldAnimate ? '600ms' : '0ms' }}
          >
            {isExternal ? (
              <a href={ctaLink} className={ctaClassName} style={ctaStyle}>
                {ctaInner}
              </a>
            ) : (
              <Link to={ctaLink} className={ctaClassName} style={ctaStyle}>
                {ctaInner}
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
